import { asyncToGenerator as _asyncToGenerator } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { lookupReset, lookupRequest } from '../../modules/lookups/actions.js';
import { getLookupData } from '../../modules/lookups/selectors.js';
import { compareParams } from '../../modules/utility/params.js';

/**
 * A hook to return lookup data for a previously registered lookup
 * @param {string|object} lookup A lookup that has already been registered
 * @param {*} params Optional, parameters that will be passed to the lookup action
 * @param {object} options Additional options for the request
 * @param {number} options.key Optional, unique key for the request. If omitted will be automatically generated from params, otherwise the default key of '0' will be used
 * @param {boolean} options.cache Optional, override the lookup cache setting
 * @param {number} options.cacheExpiry Optional, override the lookup cacheExpiry setting
 * @param {number} options.cacheLimit Optional, override the lookup cacheLimit setting
 * @param {function} options.transform Optional, override the lookup transform setting
 * @param {boolean} options.resetOnMount Optional, whether lookup data should be reset when the component is mounted
 * @param {boolean} options.resetOnUnmount Optional, whether lookup data should be reset when the component is unmounted
 * @returns {[]} An array in the format: [lookupData, resetLookup]
 * @returns {object} `lookupData` The current data for the lookup: { loading, slowLoading, params, date, response, error }
 * @returns {function} `resetLookup` A function to reset the lookup and re-request new data
 */

var useLookup = function useLookup(lookup, params) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var lookupData = useSelector(function (state) {
    return getLookupData(state, lookup, params, options);
  });
  var dispatch = useDispatch();
  useEffect(function () {
    if (options.resetOnMount) {
      dispatch(lookupReset(lookup, params, options));
    }

    dispatch(lookupRequest(lookup, params, options));
    return function () {
      if (options.resetOnUnmount) {
        dispatch(lookupReset(lookup, params, options));
      }
    };
  }, []);
  var date = lookupData.date;
  var prevDate = useRef(date);
  var prevParams = useRef(params);
  useEffect(function () {
    // re-request if lookup was reset or params changed
    if (!date && prevDate || !compareParams(params, prevParams.current)) {
      dispatch(lookupRequest(lookup, params, options));
    }

    prevDate.current = date;
    prevParams.current = params;
  }, [date, params]);
  var resetLookup = useCallback( /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/regeneratorRuntime.mark(function _callee() {
    return regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            dispatch(lookupReset(lookup, params, options));
            _context.next = 3;
            return dispatch(lookupRequest(lookup, params, options));

          case 3:
            return _context.abrupt("return", _context.sent);

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), [dispatch, lookupReset, lookupRequest, lookup, params, options]);
  return [lookupData, resetLookup];
};

var useLookup$1 = useLookup;

export { useLookup$1 as default };
